<template>
    <div class="drive-explorer">
        <div class="drive-header">
            <div class="tabs">
                <a-radio-group v-model="activeTab" button-style="solid">
                    <a-radio-button value="myDrive">{{ $t("googleDrive.myDrive") }}</a-radio-button>
                    <a-radio-button value="sharedDrives">{{ $t("googleDrive.sharedDrives") }}</a-radio-button>
                </a-radio-group>
            </div>
            <div class="actions">
                <a-input-search
                    v-model="searchQuery"
                    :placeholder="$t('googleDrive.searchFiles')"
                    style="width: 300px"
                    @search="handleSearch"
                    allowClear
                />
                <a-button-group>
                    <a-button :type="viewMode === 'grid' ? 'primary' : 'default'" @click="viewMode = 'grid'">
                        <a-icon type="appstore" />
                    </a-button>
                    <a-button :type="viewMode === 'list' ? 'primary' : 'default'" @click="viewMode = 'list'">
                        <a-icon type="bars" />
                    </a-button>
                </a-button-group>
            </div>
        </div>

        <div class="breadcrumb-container" v-if="activeTab === 'myDrive' && !isSearchMode">
            <a-breadcrumb>
                <a-breadcrumb-item>
                    <a @click="navigateToRoot">
                        <a-icon type="home" />
                    </a>
                </a-breadcrumb-item>
                <a-breadcrumb-item v-for="folder in breadcrumbs" :key="folder.id">
                    <a @click="navigateToFolder(folder.id, folder.name)">{{ folder.name }}</a>
                </a-breadcrumb-item>
            </a-breadcrumb>
        </div>

        <div class="shared-drives-list" v-if="activeTab === 'sharedDrives' && !selectedDriveId">
            <div class="loading" v-if="loadingDrives">
                <a-spin />
            </div>
            <div class="drives-grid" v-else>
                <div
                    class="drive-card"
                    v-for="drive in sharedDrives"
                    :key="drive.id"
                    @click="selectSharedDrive(drive)"
                >
                    <a-icon type="folder" class="drive-icon" />
                    <span class="drive-name">{{ drive.name }}</span>
                </div>
                <div class="empty-state" v-if="sharedDrives.length === 0">
                    <a-icon type="inbox" />
                    <p>{{ $t("googleDrive.noSharedDrives") }}</p>
                </div>
            </div>
        </div>

        <div class="files-container" v-else>
            <div class="loading" v-if="loading">
                <a-spin />
            </div>
            <template v-else>
                <div class="files-grid" v-if="viewMode === 'grid'">
                    <div
                        class="file-card"
                        :class="{ selected: isSelected(file), folder: isFolder(file) }"
                        v-for="file in files"
                        :key="file.id"
                        @click="handleFileClick($event, file)"
                        @dblclick="handleFileDblClick($event, file)"
                    >
                        <div class="file-icon">
                            <a-icon v-if="isFolder(file)" type="folder" theme="filled" />
                            <img v-else-if="file.thumbnailLink" :src="file.thumbnailLink" />
                            <a-icon v-else :type="getFileIcon(file.mimeType)" />
                        </div>
                        <div class="file-name" :title="file.name">{{ file.name }}</div>
                        <div class="file-meta">
                            <span v-if="file.size">{{ formatBytes(file.size) }}</span>
                        </div>
                    </div>
                </div>

                <a-table
                    v-else
                    :columns="columns"
                    :data-source="files"
                    :row-key="(record) => record.id"
                    :pagination="false"
                    :row-selection="{ selectedRowKeys: selectedFiles, onChange: onSelectChange }"
                    :customRow="customRow"
                >
                    <template slot="name" slot-scope="text, record">
                        <div class="file-name-cell">
                            <a-icon v-if="isFolder(record)" type="folder" theme="filled" class="folder-icon" />
                            <img v-else-if="record.thumbnailLink" :src="record.thumbnailLink" class="thumbnail" />
                            <a-icon v-else :type="getFileIcon(record.mimeType)" class="file-type-icon" />
                            <span>{{ text }}</span>
                        </div>
                    </template>
                    <template slot="size" slot-scope="text">
                        {{ text ? formatBytes(text) : '-' }}
                    </template>
                    <template slot="modifiedTime" slot-scope="text">
                        {{ formatDate(text) }}
                    </template>
                </a-table>

                <div class="empty-state" v-if="files.length === 0 && !loading">
                    <a-icon type="inbox" />
                    <p>{{ $t("googleDrive.noFiles") }}</p>
                </div>
            </template>

            <div class="load-more" v-if="nextPageToken && !loading">
                <a-button @click="loadMore" :loading="loadingMore">
                    {{ $t("googleDrive.loadMore") }}
                </a-button>
            </div>
        </div>

        <div class="selection-actions" v-if="selectedFiles.length > 0">
            <span>{{ selectedFiles.length }} {{ $t("googleDrive.filesSelected") }}</span>
            <a-button type="primary" @click="downloadSelected" :loading="downloading">
                <a-icon type="download" />
                {{ $t("googleDrive.downloadToSupsis") }}
            </a-button>
            <a-button @click="clearSelection">
                {{ $t("message.cancel") }}
            </a-button>
        </div>

        <!-- File Preview Modal -->
        <FilePreviewModal
            :visible="previewVisible"
            :files="previewableFiles"
            :initial-index="previewIndex"
            :channel-id="channel._id"
            @close="closePreview"
            @downloaded="onFileDownloaded"
        />
    </div>
</template>

<script>
import { GOOGLE_DRIVE_API } from "../../../../../api/google-drive";
import FilePreviewModal from "../../../../../components/google-drive/FilePreviewModal.vue";
import moment from "moment";

export default {
    name: "DriveExplorer",
    components: {
        FilePreviewModal,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            activeTab: "myDrive",
            viewMode: "grid",
            searchQuery: "",
            isSearchMode: false,
            loading: false,
            loadingMore: false,
            loadingDrives: false,
            downloading: false,
            files: [],
            sharedDrives: [],
            selectedFiles: [],
            nextPageToken: null,
            currentFolderId: null,
            selectedDriveId: null,
            breadcrumbs: [],
            previewVisible: false,
            previewIndex: 0,
            columns: [
                {
                    title: this.$t("googleDrive.fileName"),
                    dataIndex: "name",
                    key: "name",
                    scopedSlots: { customRender: "name" },
                },
                {
                    title: this.$t("googleDrive.fileSize"),
                    dataIndex: "size",
                    key: "size",
                    width: 120,
                    scopedSlots: { customRender: "size" },
                },
                {
                    title: this.$t("googleDrive.modifiedTime"),
                    dataIndex: "modifiedTime",
                    key: "modifiedTime",
                    width: 180,
                    scopedSlots: { customRender: "modifiedTime" },
                },
            ],
        };
    },
    methods: {
        async fetchFiles() {
            this.loading = true;
            try {
                const response = await GOOGLE_DRIVE_API.listFiles(
                    this.channel._id,
                    this.currentFolderId,
                    null,
                    this.selectedDriveId
                );
                this.files = response.data.files || [];
                this.nextPageToken = response.data.nextPageToken;
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorLoadingFiles"));
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        async loadMore() {
            if (!this.nextPageToken) return;
            this.loadingMore = true;
            try {
                const response = await GOOGLE_DRIVE_API.listFiles(
                    this.channel._id,
                    this.currentFolderId,
                    this.nextPageToken,
                    this.selectedDriveId
                );
                this.files = this.files.concat(response.data.files || []);
                this.nextPageToken = response.data.nextPageToken;
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorLoadingFiles"));
            } finally {
                this.loadingMore = false;
            }
        },
        async fetchSharedDrives() {
            this.loadingDrives = true;
            try {
                const response = await GOOGLE_DRIVE_API.listSharedDrives(this.channel._id);
                this.sharedDrives = response.data.drives || [];
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorLoadingDrives"));
            } finally {
                this.loadingDrives = false;
            }
        },
        async handleSearch() {
            if (!this.searchQuery.trim()) {
                this.isSearchMode = false;
                this.fetchFiles();
                return;
            }
            this.isSearchMode = true;
            this.loading = true;
            try {
                const response = await GOOGLE_DRIVE_API.searchFiles(
                    this.channel._id,
                    this.searchQuery,
                    null,
                    this.selectedDriveId
                );
                this.files = response.data.files || [];
                this.nextPageToken = response.data.nextPageToken;
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorSearching"));
            } finally {
                this.loading = false;
            }
        },
        handleFileClick(event, file) {
            // Cmd (Mac) veya Ctrl (Windows/Linux) + click ile yeni sekmede aç
            if (event.metaKey || event.ctrlKey) {
                if (!this.isFolder(file)) {
                    this.openInNewTab(file);
                }
                return;
            }
            if (this.isFolder(file)) return;
            var index = this.selectedFiles.indexOf(file.id);
            if (index > -1) {
                this.selectedFiles.splice(index, 1);
            } else {
                this.selectedFiles.push(file.id);
            }
        },
        handleFileDblClick(event, file) {
            if (this.isFolder(file)) {
                this.navigateToFolder(file.id, file.name);
            } else {
                this.openPreview(file);
            }
        },
        openInNewTab(file) {
            var url = file.webViewLink;
            if (!url) {
                url = "https://drive.google.com/file/d/" + file.id + "/view";
            }
            window.open(url, "_blank");
        },
        openPreview(file) {
            var nonFolderFiles = this.files.filter(function(f) {
                return f.mimeType !== "application/vnd.google-apps.folder";
            });
            var fileIndex = nonFolderFiles.findIndex(function(f) {
                return f.id === file.id;
            });
            this.previewIndex = fileIndex >= 0 ? fileIndex : 0;
            this.previewVisible = true;
        },
        closePreview() {
            this.previewVisible = false;
        },
        onFileDownloaded(asset) {
            this.$message.success(this.$t("googleDrive.fileDownloaded"));
        },
        customRow(record) {
            var self = this;
            return {
                on: {
                    click: function(event) {
                        self.handleFileClick(event, record);
                    },
                    dblclick: function(event) {
                        self.handleFileDblClick(event, record);
                    },
                },
            };
        },
        navigateToFolder(folderId, folderName) {
            this.currentFolderId = folderId;
            if (folderName) {
                const existingIndex = this.breadcrumbs.findIndex((b) => b.id === folderId);
                if (existingIndex > -1) {
                    this.breadcrumbs = this.breadcrumbs.slice(0, existingIndex + 1);
                } else {
                    this.breadcrumbs.push({ id: folderId, name: folderName });
                }
            }
            this.selectedFiles = [];
            this.fetchFiles();
        },
        navigateToRoot() {
            this.currentFolderId = null;
            this.breadcrumbs = [];
            this.selectedFiles = [];
            this.fetchFiles();
        },
        selectSharedDrive(drive) {
            this.selectedDriveId = drive.id;
            this.breadcrumbs = [{ id: drive.id, name: drive.name }];
            this.fetchFiles();
        },
        isFolder(file) {
            return file.mimeType === "application/vnd.google-apps.folder";
        },
        isSelected(file) {
            return this.selectedFiles.includes(file.id);
        },
        onSelectChange(selectedRowKeys) {
            this.selectedFiles = selectedRowKeys;
        },
        clearSelection() {
            this.selectedFiles = [];
        },
        async downloadSelected() {
            if (this.selectedFiles.length === 0) return;
            this.downloading = true;
            try {
                for (const fileId of this.selectedFiles) {
                    const response = await GOOGLE_DRIVE_API.downloadFile(this.channel._id, fileId);
                    if (response.data.asset) {
                        this.$message.success(this.$t("googleDrive.fileDownloaded"));
                    }
                }
                this.selectedFiles = [];
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorDownloading"));
            } finally {
                this.downloading = false;
            }
        },
        formatBytes(bytes) {
            if (!bytes) return "0 B";
            const k = 1024;
            const sizes = ["B", "KB", "MB", "GB", "TB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        getFileIcon(mimeType) {
            if (!mimeType) return "file";
            if (mimeType.includes("image")) return "file-image";
            if (mimeType.includes("video")) return "video-camera";
            if (mimeType.includes("audio")) return "sound";
            if (mimeType.includes("pdf")) return "file-pdf";
            if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return "file-excel";
            if (mimeType.includes("document") || mimeType.includes("word")) return "file-word";
            if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "file-ppt";
            if (mimeType.includes("zip") || mimeType.includes("compressed")) return "file-zip";
            return "file";
        },
    },
    computed: {
        previewableFiles() {
            return this.files.filter(function(f) {
                return f.mimeType !== "application/vnd.google-apps.folder";
            });
        },
    },
    watch: {
        activeTab(val) {
            this.files = [];
            this.selectedFiles = [];
            this.breadcrumbs = [];
            this.currentFolderId = null;
            this.selectedDriveId = null;
            this.isSearchMode = false;
            this.searchQuery = "";
            if (val === "myDrive") {
                this.fetchFiles();
            } else {
                this.fetchSharedDrives();
            }
        },
    },
    mounted() {
        this.fetchFiles();
    },
};
</script>

<style scoped lang="scss">
.drive-explorer {
    padding: 20px;
    height: calc(100vh - 200px);
    overflow-y: auto;

    .drive-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;

        .actions {
            display: flex;
            gap: 10px;
        }
    }

    .breadcrumb-container {
        margin-bottom: 20px;
        padding: 10px 15px;
        background: var(--color-bg-secondary);
        border-radius: 8px;
    }

    .shared-drives-list {
        .drives-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
            gap: 16px;

            .drive-card {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 20px;
                background: var(--color-bg-default);
                border: 1px solid var(--color-border-default);
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s;

                &:hover {
                    border-color: #4285f4;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

                .drive-icon {
                    font-size: 48px;
                    color: #4285f4;
                    margin-bottom: 10px;
                }

                .drive-name {
                    font-weight: 500;
                    text-align: center;
                    word-break: break-word;
                }
            }
        }
    }

    .files-container {
        .files-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
            gap: 16px;

            .file-card {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 15px;
                background: var(--color-bg-default);
                border: 1px solid var(--color-border-default);
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s;

                &:hover {
                    border-color: #4285f4;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

                &.selected {
                    border-color: #4285f4;
                    background: rgba(66, 133, 244, 0.1);
                }

                &.folder {
                    .file-icon {
                        color: #f9ab00;
                    }
                }

                .file-icon {
                    font-size: 48px;
                    margin-bottom: 10px;
                    color: #5f6368;

                    img {
                        width: 48px;
                        height: 48px;
                        object-fit: cover;
                        border-radius: 4px;
                    }
                }

                .file-name {
                    font-size: 13px;
                    font-weight: 500;
                    text-align: center;
                    word-break: break-word;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    max-width: 100%;
                }

                .file-meta {
                    font-size: 11px;
                    color: var(--color-text-secondary);
                    margin-top: 5px;
                }
            }
        }

        .file-name-cell {
            display: flex;
            align-items: center;
            gap: 10px;

            .folder-icon {
                color: #f9ab00;
                font-size: 20px;
            }

            .thumbnail {
                width: 24px;
                height: 24px;
                object-fit: cover;
                border-radius: 4px;
            }

            .file-type-icon {
                font-size: 20px;
                color: #5f6368;
            }
        }
    }

    .loading {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 200px;
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 200px;
        color: var(--color-text-secondary);

        .anticon {
            font-size: 48px;
            margin-bottom: 16px;
        }
    }

    .load-more {
        display: flex;
        justify-content: center;
        margin-top: 20px;
    }

    .selection-actions {
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 15px 25px;
        background: var(--color-bg-default);
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        z-index: 100;
    }
}
</style>
